import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';

function fmt(n, dec = 0) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString('nl-BE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

export default function InvoicesPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [conns, setConns]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ energy_type: '', year: '', connection_id: '' });

  useEffect(() => { api.get('/api/connections').then(r => setConns(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.energy_type)   params.energy_type = filters.energy_type;
    if (filters.year)          params.year = filters.year;
    if (filters.connection_id) params.connection_id = filters.connection_id;
    api.get('/api/invoices', { params })
      .then(r => setInvoices(r.data))
      .finally(() => setLoading(false));
  }, [filters]);

  function set(field, val) { setFilters(f => ({ ...f, [field]: val })); }

  const years = Array.from(
    new Set(invoices.map(i => i.period_end && i.period_end.slice(0, 4)).filter(Boolean))
  ).sort().reverse();

  function exportExcel() {
    const params = new URLSearchParams();
    if (filters.energy_type)   params.set('energy_type', filters.energy_type);
    if (filters.year)          params.set('year', filters.year);
    if (filters.connection_id) params.set('connection_id', filters.connection_id);
    api.get(`/api/exports/invoices.xlsx?${params.toString()}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url; a.download = 'engie-facturen.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  return (
    <>
      <div className="page-header">
        <h2>{t('invoices_title')}</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={exportExcel}>
            <i className="bi bi-file-earmark-excel me-1" />{t('inv_export')}
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/invoices/new')}>
            <i className="bi bi-pencil me-1" />{t('inv_new')}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/invoices/upload')}>
            <i className="bi bi-upload me-1" />{t('inv_upload')}
          </button>
        </div>
      </div>

      <div className="page-body">
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filters.energy_type} onChange={e => set('energy_type', e.target.value)}>
            <option value="">{t('all')} — {t('inv_filter_type')}</option>
            <option value="elektriciteit">{t('type_elektriciteit')}</option>
            <option value="gas">{t('type_gas')}</option>
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filters.year} onChange={e => set('year', e.target.value)}>
            <option value="">{t('all')} — {t('inv_filter_year')}</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
            value={filters.connection_id} onChange={e => set('connection_id', e.target.value)}>
            <option value="">{t('all')} — {t('inv_connection')}</option>
            {conns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        <div className="card">
          {loading ? (
            <div className="card-body text-center text-muted py-4">
              <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
            </div>
          ) : invoices.length === 0 ? (
            <div className="card-body text-center text-muted py-5">{t('no_data')}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-clickable mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('inv_connection')}</th>
                    <th>{t('inv_period')}</th>
                    <th>{t('inv_type')}</th>
                    <th className="text-end">{t('inv_consumption')}</th>
                    <th className="text-end">{t('inv_amount')}</th>
                    <th>{t('inv_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(i => (
                    <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}>
                      <td>
                        <span className={`badge badge-${i.energy_type} me-2`}>{t(`type_${i.energy_type}`)}</span>
                        {i.connection_label}
                      </td>
                      <td className="text-muted" style={{ fontSize: 13 }}>
                        {i.period_start ? i.period_start.slice(0, 10) : '?'} → {i.period_end ? i.period_end.slice(0, 10) : '?'}
                      </td>
                      <td>{t(`itype_${i.invoice_type}`)}</td>
                      <td className="text-end">
                        {fmt(i.consumption_total)} {i.energy_type === 'gas' ? 'm³' : 'kWh'}
                      </td>
                      <td className="text-end fw-medium">€ {fmt(i.amount_incl_vat, 2)}</td>
                      <td><span className={`badge badge-${i.parse_status}`}>{t(`pstatus_${i.parse_status}`)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
